/**
 * PostListView — 게시글 목록 (스타일 시스템 적용)
 *
 * Board의 display_style + 반응형 로직(resolveDisplayStyle)에 따라
 * 5가지 렌더러 중 적절한 것을 선택하여 표시.
 * QnA 게시판일 때 상단에 상태 필터 탭 표시.
 */
import React, { useState, useMemo, useEffect, useCallback, memo } from 'react';
import { useResponsive } from '../../hooks/useResponsive';
import { Trans, t } from '@lingui/macro';
import { useBoardContext } from '../../context/BoardContext';
import { Post, DisplayStyle, IssueStatus, ISSUE_STATUS_CONFIG, getIssueMetadata, canWritePost, normalizeDisplayStyle } from '../../models/Board';
import { useMyRole } from '../../context/WorkspacePermissionContext';
import { useWorkspaceSettings } from '../../context/WorkspaceSettingsContext';
import { cn } from '../../lib/utils';
import { PenLine, Tag, X, ChevronDown, User, Search } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { My } from '../../services/My';
import { MyNotifs } from '../../services/MyNotifs';
import { incrementViewCount, updateIssueStatus, getUnreadCommentPostIds, getUsedTags } from '../../services/BoardService';
import PostCreateModal from './PostCreateModal';
import {
  DiscourseRenderer,
  BasecampRenderer,
  NotionRenderer,
  WikiRenderer,
} from './PostStyleRenderers';
import Pagination from './Pagination';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';

/** 렌더러 매핑 (canonical 3 + wiki) — legacy 값('dooray'/'linear')은 normalizeDisplayStyle 에서 매핑됨 */
const RENDERERS: Record<DisplayStyle, React.FC<any>> = {
  discourse: DiscourseRenderer,
  basecamp: BasecampRenderer,
  notion: NotionRenderer,
  wiki: WikiRenderer,
};

/** QnA 필터 타입 */
type QnaFilter = 'all' | 'unresolved' | 'resolved' | 'mine';

function useQnaFilters(): { value: QnaFilter; label: string }[] {
  return [
    { value: 'all',        label: t`All` },
    { value: 'unresolved', label: t`Unresolved` },
    { value: 'resolved',   label: t`Resolved` },
    { value: 'mine',       label: t`My questions` },
  ];
}

/** Issue 필터 타입 */
type IssueFilter = 'all' | 'active' | 'done' | 'mine';

function useIssueFilters(): { value: IssueFilter; label: string }[] {
  return [
    { value: 'all',    label: t`All` },
    { value: 'active', label: t`Active` },
    { value: 'done',   label: t`Done` },
    { value: 'mine',   label: t`My issues` },
  ];
}

/**
 * BoardSearchBox — 검색 입력창 (입력 상태를 자체 보유)
 *
 * localSearch 를 컴포넌트 내부 state 로 두어, 검색 결과(posts) 변경으로
 * 부모 PostListView 가 재렌더돼도 memo 덕분에 이 입력창은 재렌더되지 않음.
 * (타이핑 → 이 컴포넌트만 재렌더, 결과 목록은 무관)
 * 보드 전환 시엔 key={board.id} 로 재마운트되며 입력이 자동 리셋됨.
 */
const BoardSearchBox = memo(function BoardSearchBox({
  placeholder,
  onSubmit,
  onClear,
}: {
  placeholder: string;
  onSubmit: (q: string) => void;
  onClear: () => void;
}) {
  const [local, setLocal] = useState('');
  return (
    <div className="flex-1 min-w-0 max-w-[460px] flex items-center gap-1.5 h-8 px-2.5 rounded-lg border border-border bg-muted/40 focus-within:bg-background focus-within:border-primary transition-colors">
      <Search className="size-3.5 text-muted-foreground shrink-0" />
      <input
        type="text"
        value={local}
        onChange={e => setLocal(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter') { if (local.trim()) onSubmit(local.trim()); }
          if (e.key === 'Escape') { setLocal(''); onClear(); }
        }}
        placeholder={placeholder}
        className="flex-1 min-w-0 bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none"
      />
      {local && (
        <button
          onClick={() => { setLocal(''); onClear(); }}
          className="size-5 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors cursor-pointer shrink-0"
          aria-label={t`Clear search`}
        >
          <X className="size-3" />
        </button>
      )}
    </div>
  );
});

const PostListView: React.FC<{
  displayStyle?: DisplayStyle;
  /** 워크스페이스 보드처럼 PostListView 가 헤더 액션(검색·새글)도 직접 제공해야 하는지 여부.
   *  채널 inline view 는 별도 헤더에서 처리하므로 기본 false. */
  showActions?: boolean;
}> = ({ displayStyle, showActions = false }) => {
  const { posts, loading, setSelectedPost, selectedPost, selectedBoard, totalPosts, refreshPosts, searchQuery, setSearchQuery } = useBoardContext();
  const { isMobile } = useResponsive();
  const [showPostCreate, setShowPostCreate] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [qnaFilter, setQnaFilter] = useState<QnaFilter>('all');
  const [issueFilter, setIssueFilter] = useState<IssueFilter>('all');
  const qnaFilters = useQnaFilters();
  const issueFilters = useIssueFilters();
  const [tagFilters, setTagFilters] = useState<string[]>([]);
  const [authorFilter, setAuthorFilter] = useState<string | null>(null);
  const [showTagDropdown, setShowTagDropdown] = useState(false);
  const [showAuthorDropdown, setShowAuthorDropdown] = useState(false);
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const myRole = useMyRole();
  const wsSettings = useWorkspaceSettings();
  const canWrite = selectedBoard ? canWritePost(selectedBoard, myRole ?? 'guest', My.user?.id, wsSettings.board_manager_user_ids) : false;

  // 검색 핸들러 — 입력 상태는 BoardSearchBox 가 보유, 여기선 결과 쿼리만 갱신
  // (useCallback 으로 안정화해 memo 된 BoardSearchBox 가 결과 변경 시 재렌더되지 않도록)
  const handleSearchSubmit = useCallback((q: string) => {
    const trimmed = q.trim();
    if (!trimmed) return;
    setSearchQuery(trimmed);
  }, [setSearchQuery]);
  const handleSearchClear = useCallback(() => {
    setSearchQuery('');
  }, [setSearchQuery]);

  const isQna = selectedBoard?.type === 'qna';
  const isIssue = selectedBoard?.type === 'issue';

  // 사용된 태그 로드 (보드 변경 시)
  useEffect(() => {
    if (!selectedBoard?.id) return;
    setTagFilters([]);
    setAuthorFilter(null);
    const defaultTags = selectedBoard.default_tags ?? [];
    getUsedTags(selectedBoard.id).then(used => {
      const merged = new Set([...defaultTags, ...used]);
      setAvailableTags(Array.from(merged));
    }).catch(() => setAvailableTags(defaultTags));
  }, [selectedBoard?.id]);

  // 현재 로드된 posts 의 태그도 합산 — getUsedTags(전체 쿼리)가 비거나 실패해도
  // 목록에 보이는 태그는 필터할 수 있게 (검색 결과/페이지 단위 포함)
  useEffect(() => {
    if (posts.length === 0) return;
    const postTags = new Set<string>();
    posts.forEach(p => p.tags?.forEach(tag => postTags.add(tag)));
    if (postTags.size === 0) return;
    setAvailableTags(prev => {
      const merged = Array.from(new Set([...prev, ...postTags]));
      return merged.length === prev.length ? prev : merged;
    });
  }, [posts]);

  // 고유 작성자 목록 (현재 페이지)
  const uniqueAuthors = useMemo(() => {
    const map = new Map<string, { uid: string; name: string }>();
    posts.forEach(p => {
      if (!map.has(p.author.uid)) {
        map.set(p.author.uid, { uid: p.author.uid, name: p.author.name });
      }
    });
    return Array.from(map.values());
  }, [posts]);
  // 미읽음 댓글 게시글 추적 (목록 로드 시 + 상세 닫을 때 갱신)
  const [unreadPostIds, setUnreadPostIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    const userId = My.user?.id;
    if (!userId || posts.length === 0) {
      setUnreadPostIds(new Set());
      return;
    }
    const postsWithComments = posts.filter(p => p.comment_count > 0);
    if (postsWithComments.length === 0) {
      setUnreadPostIds(new Set());
      return;
    }
    getUnreadCommentPostIds(
      postsWithComments.map(p => p.id),
      userId,
    ).then(setUnreadPostIds).catch(() => setUnreadPostIds(new Set()));
  }, [posts, selectedBoard?.id, selectedPost?.id]);

  // 통지센터 알림(미읽음)이 있는 게시글 → 제목 옆 오렌지 점 (MyNotifs 실시간 구독)
  const [notifiedPostIds, setNotifiedPostIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    const compute = () => setNotifiedPostIds(new Set(
      MyNotifs.Notifications.filter(n => n.isNew && n.source === 'board' && n.postId).map(n => n.postId!),
    ));
    compute();
    return MyNotifs.subscribeToNotificationUpdates(compute);
  }, []);

  // 외부에서 전달된 스타일 사용 (wiki는 항상 wiki). legacy DB 값('dooray'/'linear')은 normalize.
  const baseStyle: DisplayStyle = selectedBoard?.type === 'wiki'
    ? 'wiki'
    : (normalizeDisplayStyle(displayStyle ?? null) ?? 'discourse');
  // 모바일(좁은 컨테이너) — 테이블형(notion)은 헤더가 잘려 포럼형(discourse)으로 강제 표시
  const resolvedStyle: DisplayStyle = isMobile && baseStyle === 'notion' ? 'discourse' : baseStyle;

  // 필터링 (클라이언트 사이드)
  const filteredPosts = useMemo(() => {
    let result = posts;

    // QnA 필터
    if (isQna && qnaFilter !== 'all') {
      result = result.filter(post => {
        const isResolved = post.metadata?.is_resolved === true;
        if (qnaFilter === 'resolved') return isResolved;
        if (qnaFilter === 'unresolved') return !isResolved;
        if (qnaFilter === 'mine') return post.author.uid === My.user?.id;
        return true;
      });
    }

    // Issue 필터
    if (isIssue && issueFilter !== 'all') {
      result = result.filter(post => {
        const issue = getIssueMetadata(post);
        const status = issue?.status ?? 'todo';
        if (issueFilter === 'active') return !['done', 'cancelled'].includes(status);
        if (issueFilter === 'done') return status === 'done' || status === 'cancelled';
        if (issueFilter === 'mine') {
          return issue?.assignee?.uid === My.user?.id || post.author.uid === My.user?.id;
        }
        return true;
      });
    }

    // 태그 필터 — 다중 선택 AND (모든 선택 태그를 포함하는 글만)
    if (tagFilters.length > 0) {
      result = result.filter(post => tagFilters.every(t => post.tags?.includes(t)));
    }

    // 작성자 필터
    if (authorFilter) {
      result = result.filter(post => post.author.uid === authorFilter);
    }

    return result;
  }, [posts, qnaFilter, issueFilter, isQna, isIssue, tagFilters, authorFilter]);

  const hasActiveFilter = tagFilters.length > 0 || authorFilter !== null;

  const handlePostClick = async (post: Post) => {
    setSelectedPost(post);
    if (My.user?.id) {
      incrementViewCount(post.id, My.user.id).catch(() => {});
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    if (selectedBoard) {
      refreshPosts(selectedBoard.id, page);
    }
  };

  if (loading && posts.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center h-full">
        <div className="flex flex-col items-center gap-3">
          <div className="size-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
          <span className="text-sm text-muted-foreground"><Trans>Loading posts…</Trans></span>
        </div>
      </div>
    );
  }

  // posts 0 일 때 본문 영역에 보여줄 빈 상태 (필터바/검색창은 항상 유지하므로 early return 하지 않음)
  const emptyContent = searchQuery.trim() ? (
    <div className="flex-1 flex flex-col items-center justify-center gap-3 h-full px-6">
      <div className="size-16 rounded-2xl bg-muted flex items-center justify-center">
        <Search className="size-7 text-muted-foreground" strokeWidth={1.5} />
      </div>
      <div className="text-center">
        <p className="text-base font-semibold text-foreground">
          <Trans>No search results</Trans>
        </p>
        <p className="text-sm text-muted-foreground mt-1.5 max-w-[280px]">
          <Trans>No posts match your search. Try a different keyword.</Trans>
        </p>
      </div>
    </div>
  ) : (
    <div className="flex-1 flex flex-col items-center justify-center gap-5 h-full px-6">
      <div className="size-20 rounded-2xl bg-gradient-to-br from-primary/15 to-primary/5 flex items-center justify-center">
        <PenLine className="size-8 text-primary" strokeWidth={1.5} />
      </div>
      <div className="text-center">
        <p className="text-base font-semibold text-foreground">
          <Trans>No posts yet</Trans>
        </p>
        <p className="text-sm text-muted-foreground mt-1.5 max-w-[260px]">
          {(() => {
            const boardName = selectedBoard?.name ?? t`Boards`;
            return t`Write the first post in ${boardName}`;
          })()}
        </p>
      </div>
      {canWrite && (
        <>
          <Button size="sm" onClick={() => setShowPostCreate(true)} className="mt-1">
            <PenLine className="size-4 mr-1.5" />
            <Trans>New post</Trans>
          </Button>
          <PostCreateModal open={showPostCreate} onOpenChange={setShowPostCreate} />
        </>
      )}
    </div>
  );

  const Renderer = RENDERERS[resolvedStyle];

  return (
    <div className="flex flex-col h-full">
      {/* ── 공통 필터바 — 좌: 새 글 작성, 우: 태그/작성자/초기화/검색 ── */}
      {(availableTags.length > 0 || uniqueAuthors.length > 1 || showActions || selectedBoard) && (
        <div className="flex items-center gap-2 px-4 lg:px-5 py-2 border-b border-border shrink-0 bg-background/80 backdrop-blur-sm">
          {/* 새 글 작성 — 필터바 좌측 끝 */}
          {showActions && canWrite && (
            <Button size="sm" onClick={() => setShowPostCreate(true)}>
              <PenLine className="size-3.5 mr-1.5" />
              <span className="hidden sm:inline"><Trans>New post</Trans></span>
              <span className="sm:hidden"><Trans>Write</Trans></span>
            </Button>
          )}

          {/* 검색창 — 기본 표시 (왼쪽, flex-1). 입력 상태는 BoardSearchBox 가 자체 보유(memo) */}
          {selectedBoard && (
            <BoardSearchBox
              key={selectedBoard.id}
              placeholder={t`Search by title or body…`}
              onSubmit={handleSearchSubmit}
              onClear={handleSearchClear}
            />
          )}

          {/* 우측 그룹 시작 — 태그/작성자/초기화 */}
          <div className="ml-auto flex items-center gap-2 flex-wrap justify-end shrink-0">
          {/* 태그 필터 — Popover (Radix Portal 로 body 마운트해서 부모 layout 영향 X) */}
          {availableTags.length > 0 && (
            <Popover open={showTagDropdown} onOpenChange={setShowTagDropdown} modal>
              <PopoverTrigger asChild>
                <button
                  className={cn(
                    'inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer border',
                    tagFilters.length > 0
                      ? 'border-primary/30 bg-primary/10 text-primary'
                      : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground',
                  )}
                >
                  <Tag className="size-3" />
                  {tagFilters.length === 0
                    ? t`Tags`
                    : tagFilters.length === 1
                      ? tagFilters[0]
                      : `${tagFilters[0]} +${tagFilters.length - 1}`}
                  {tagFilters.length > 0 ? (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); setTagFilters([]); setShowTagDropdown(false); }}
                      className="hover:text-destructive cursor-pointer"
                    >
                      <X className="size-3" />
                    </span>
                  ) : (
                    <ChevronDown className="size-3" />
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-56 p-0 max-h-64 overflow-y-auto" align="start" side="bottom" sideOffset={4}>
                <div className="px-3 py-1.5 text-meta-sm font-semibold text-muted-foreground uppercase tracking-wider border-b border-border/50 mb-1 flex items-center justify-between">
                  <span><Trans>Tags (AND)</Trans></span>
                  {tagFilters.length > 0 && (
                    <button
                      onClick={() => setTagFilters([])}
                      className="text-meta-sm font-normal text-muted-foreground hover:text-destructive normal-case tracking-normal"
                    ><Trans>Clear all</Trans></button>
                  )}
                </div>
                {availableTags.map(tag => {
                  const isActive = tagFilters.includes(tag);
                  return (
                    <button
                      key={tag}
                      onClick={() => setTagFilters(prev => isActive ? prev.filter(t => t !== tag) : [...prev, tag])}
                      className={cn(
                        'w-full flex items-center gap-2 px-3 py-1.5 text-label transition-colors cursor-pointer',
                        isActive
                          ? 'bg-primary/10 text-primary font-medium'
                          : 'text-foreground/80 hover:bg-muted',
                      )}
                    >
                      <span className={cn(
                        'size-3.5 rounded border flex items-center justify-center shrink-0',
                        isActive ? 'bg-primary border-primary' : 'border-border',
                      )}>
                        {isActive && <span className="text-on-primary-text text-meta-sm leading-none">✓</span>}
                      </span>
                      <Tag className="size-3 shrink-0 text-muted-foreground" />
                      <span className="truncate">{tag}</span>
                    </button>
                  );
                })}
              </PopoverContent>
            </Popover>
          )}

          {/* 작성자 필터 — Popover */}
          {uniqueAuthors.length > 1 && (
            <Popover open={showAuthorDropdown} onOpenChange={setShowAuthorDropdown} modal>
              <PopoverTrigger asChild>
                <button
                  className={cn(
                    'inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-all cursor-pointer border',
                    authorFilter
                      ? 'border-primary/30 bg-primary/10 text-primary'
                      : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground',
                  )}
                >
                  <User className="size-3" />
                  {authorFilter ? uniqueAuthors.find(a => a.uid === authorFilter)?.name ?? t`Author` : t`Author`}
                  {authorFilter ? (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={(e) => { e.stopPropagation(); setAuthorFilter(null); setShowAuthorDropdown(false); }}
                      className="hover:text-destructive cursor-pointer"
                    >
                      <X className="size-3" />
                    </span>
                  ) : (
                    <ChevronDown className="size-3" />
                  )}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-44 p-0 max-h-48 overflow-y-auto" align="start" side="bottom" sideOffset={4}>
                {uniqueAuthors.map(a => (
                  <button
                    key={a.uid}
                    onClick={() => { setAuthorFilter(a.uid); setShowAuthorDropdown(false); }}
                    className={cn(
                      'w-full flex items-center gap-2 px-3 py-1.5 text-label transition-colors cursor-pointer',
                      authorFilter === a.uid
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-foreground/80 hover:bg-muted',
                    )}
                  >
                    <User className="size-3 shrink-0 text-muted-foreground" />
                    <span className="truncate">{a.name}</span>
                  </button>
                ))}
              </PopoverContent>
            </Popover>
          )}

          {/* 필터 초기화 */}
          {hasActiveFilter && (
            <button
              onClick={() => { setTagFilters([]); setAuthorFilter(null); }}
              className="text-meta-sm text-muted-foreground hover:text-foreground transition-colors cursor-pointer"
            >
              <Trans>Reset</Trans>
            </button>
          )}
          </div>
        </div>
      )}

      {/* 검색 결과 배너 — 채널 inline view 는 헤더에 검색 input/취소가 따로 있어 '몇 건'만 표시 */}
      {searchQuery && (
        <div className="flex items-center gap-2 px-4 lg:px-5 py-2 border-b border-border shrink-0 bg-primary/5">
          <span className="text-xs text-foreground">
            <strong>&ldquo;{searchQuery}&rdquo;</strong> <Trans>search results: {totalPosts}</Trans>
          </span>
          {showActions && (
            <button
              onClick={handleSearchClear}
              className="text-xs text-primary hover:text-primary/80 font-medium transition-colors cursor-pointer"
            >
              <Trans>Clear search</Trans>
            </button>
          )}
        </div>
      )}

      {/* PostCreateModal — 필터바 액션에서 열림 (빈 상태 mount 와 별도) */}
      {showActions && <PostCreateModal open={showPostCreate} onOpenChange={setShowPostCreate} />}

      {/* QnA 필터 탭 */}
      {isQna && (
        <div className="flex items-center gap-1 px-4 lg:px-5 py-2 border-b border-border shrink-0 bg-background/80 backdrop-blur-sm">
          {qnaFilters.map(f => (
            <button
              key={f.value}
              onClick={() => setQnaFilter(f.value)}
              className={cn(
                'px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer',
                qnaFilter === f.value
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              )}
            >
              {f.label}
              {f.value !== 'all' && (() => {
                const count = posts.filter(p => {
                  const isResolved = p.metadata?.is_resolved === true;
                  if (f.value === 'resolved') return isResolved;
                  if (f.value === 'unresolved') return !isResolved;
                  if (f.value === 'mine') return p.author.uid === My.user?.id;
                  return false;
                }).length;
                return count > 0 ? <span className="ml-1 tabular-nums opacity-70">{count}</span> : null;
              })()}
            </button>
          ))}
        </div>
      )}

      {/* Issue 필터 탭 */}
      {isIssue && (
        <div className="flex items-center gap-1 px-4 lg:px-5 py-2 border-b border-border shrink-0 bg-background/80 backdrop-blur-sm">
          {issueFilters.map(f => (
            <button
              key={f.value}
              onClick={() => setIssueFilter(f.value)}
              className={cn(
                'px-3 py-1.5 rounded-full text-xs font-medium transition-all duration-200 cursor-pointer',
                issueFilter === f.value
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:bg-muted hover:text-foreground',
              )}
            >
              {f.label}
              {f.value !== 'all' && (() => {
                const count = posts.filter(p => {
                  const issue = getIssueMetadata(p);
                  const status = issue?.status ?? 'todo';
                  if (f.value === 'active') return !['done', 'cancelled'].includes(status);
                  if (f.value === 'done') return status === 'done' || status === 'cancelled';
                  if (f.value === 'mine') return issue?.assignee?.uid === My.user?.id || p.author.uid === My.user?.id;
                  return false;
                }).length;
                return count > 0 ? <span className="ml-1 tabular-nums opacity-70">{count}</span> : null;
              })()}
            </button>
          ))}
        </div>
      )}

      {/* 렌더러 — posts 0 이면 빈 상태(검색 결과 없음/첫 글), 필터바는 위에 항상 유지 */}
      <div className="flex-1 min-h-0">
        {posts.length === 0 ? emptyContent : (
          <Renderer
            posts={filteredPosts}
            onPostClick={handlePostClick}
            totalPosts={isQna ? filteredPosts.length : totalPosts}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            selectedPostId={selectedPost?.id ?? null}
            boardType={selectedBoard?.type}
            unreadPostIds={unreadPostIds}
            notifiedPostIds={notifiedPostIds}
            onStatusChange={async (postId: string, newStatus: IssueStatus) => {
              await updateIssueStatus(postId, {}, newStatus);
              if (selectedBoard) refreshPosts(selectedBoard.id);
            }}
          />
        )}
      </div>

      {/* 페이지네이션 — 글이 있을 때만 */}
      {posts.length > 0 && (
        <div className="shrink-0 border-t border-border/40">
          <Pagination
            currentPage={currentPage}
            totalItems={isQna || isIssue ? filteredPosts.length : totalPosts}
            pageSize={20}
            onPageChange={handlePageChange}
          />
        </div>
      )}
    </div>
  );
};

export default PostListView;
